/**
 * The Assistant's words, rendered from the small Markdown it writes
 * (`markdown.ts`) as React elements — never as HTML. The case's links
 * show as named links that open their view — a record's with its number on
 * the case after its label ("the filing (record 12)"), opening beside the
 * view; a page links out in a new tab.
 */

import { Fragment, useMemo, type ReactNode } from 'react';

import { Anchor, Text, Tooltip } from '@mantine/core';

import { useRecordIndex, type RecordIndex } from '@/components/Workbench/recordIndex';
import { parseCopilotText, type CaseLink, type Inline } from './markdown';
import classes from './Copilot.module.css';

const WHAT: Record<CaseLink['scheme'], string> = {
  evidence: 'Open the record',
  checklist: 'Open the checklist item',
  ask: 'Open the ask',
  task: 'See the run in the timeline',
  file: 'Open the file',
  para: 'Show the paragraph',
};

interface CopilotTextProps {
  text: string;
  /** Open a case link in its view. */
  onOpenLink: (link: CaseLink) => void;
  /** Still being written: a cursor after the last word. */
  writing?: boolean;
}

function inline(
  nodes: Inline[],
  onOpenLink: (link: CaseLink) => void,
  records: RecordIndex
): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>;
      case 'strong':
        return <strong key={index}>{inline(node.children, onOpenLink, records)}</strong>;
      case 'em':
        return <em key={index}>{inline(node.children, onOpenLink, records)}</em>;
      case 'code':
        return <code key={index}>{node.text}</code>;
      case 'web':
        return (
          <Anchor key={index} href={node.href} target="_blank" rel="noopener noreferrer" inherit>
            {node.label}
          </Anchor>
        );
      case 'link': {
        const record = node.link.scheme === 'evidence' ? records.entry(node.link.id) : undefined;
        return (
          <Tooltip
            key={index}
            label={record?.statement ?? WHAT[node.link.scheme]}
            withArrow
            openDelay={400}
            multiline
            maw={360}
          >
            <Anchor
              component="button"
              type="button"
              inherit
              className={classes.link}
              onClick={() => onOpenLink(node.link)}
              data-testid={`copilot-link-${node.link.scheme}-${node.link.id}`}
            >
              {node.label}
              {record ? ` (record ${record.number})` : ''}
            </Anchor>
          </Tooltip>
        );
      }
      default:
        return null;
    }
  });
}

export function CopilotText({ text, onOpenLink, writing = false }: Readonly<CopilotTextProps>) {
  const blocks = useMemo(() => parseCopilotText(text), [text]);
  const records = useRecordIndex();
  return (
    <Text size="sm" component="div" className={classes.text}>
      {blocks.map((block, index) => {
        const last = index === blocks.length - 1;
        if (block.kind === 'paragraph') {
          return (
            <p key={index} className={writing && last ? classes.cursor : undefined}>
              {inline(block.children, onOpenLink, records)}
            </p>
          );
        }
        const Tag = block.ordered ? 'ol' : 'ul';
        return (
          <Tag key={index} className={writing && last ? classes.cursor : undefined}>
            {block.items.map((item, i) => (
              <li key={i}>{inline(item, onOpenLink, records)}</li>
            ))}
          </Tag>
        );
      })}
    </Text>
  );
}
